/**
 * Carrying "the daily challenge this reader pressed Play on" across an OAuth
 * redirect.
 *
 * The card is on the front page for everybody, so the first press is often a
 * press by somebody who is not signed in — and the round trip to their
 * provider and back loses everything the page was holding, hash included (see
 * router.ts's note on camo/editor.ts's hasPendingCamo()). This is the third
 * user of that same mechanism, after the camo editor's unsaved work and
 * lobby-invite-stash.ts's shared lobby id, and it holds one slug.
 *
 * Its own module, importing nothing, for the same two reasons
 * lobby-invite-stash.ts has: router.ts has to peek and consume it on every
 * landing regardless of which screen ends up rendered, and a file with no
 * imports is one `node --test` can load directly.
 */

const PENDING_KEY = "lance.blue:daily:pending";

/**
 * Set immediately before the OAuth redirect fires, from the sign-in dialog the
 * Play control opens (onBeforeConnect). Set-and-forget: the tab is about to
 * leave, so there is nothing to clean up on failure.
 */
export function stashDailyPlay(slug: string): void {
  try {
    localStorage.setItem(PENDING_KEY, slug);
  } catch {
    /* Private mode or a full quota: sign-in still works and the reader lands
     * on the front page, where the card and its Play button are. */
  }
}

/**
 * Whether a stash exists, without consuming it. routeFromLocation() only ever
 * peeks: deciding the route must never by itself discard a stash nothing has
 * used yet. Mirrors hasPendingLobbyInvite() and hasPendingCamo().
 */
export function hasPendingDailyPlay(): boolean {
  try {
    return localStorage.getItem(PENDING_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Reads and clears the stash in one step. Called exactly once per landing on
 * the "daily" route, whether or not the slug it returns is used — so a visit
 * to the archive for any other reason clears a stash an abandoned sign-in
 * left, and an old Play press cannot hijack an unrelated later sign-in.
 */
export function takePendingDailyPlay(): string | null {
  let slug: string | null = null;
  try {
    slug = localStorage.getItem(PENDING_KEY);
  } catch {
    /* ignored, same as stashDailyPlay */
  }
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignored */
  }
  return slug;
}
